import type { SupportChatOptions } from '../../../shared/support-chat';
import type { TicketCategory } from '../../../shared/support-chat/state';
import { WELCOME, WELCOME_SUGGESTIONS, welcomeName } from '../../../shared/support-chat/welcome';
import { supabase } from './supabase';
import { withDemoSearch } from './data-source';

/** Above the recording player (40) and the setup action bar (30), under dialog and drawer scrims (50), so an open dialog covers the chat like the rest of the page. */
export const CHAT_LAYER = 45;
/** The widget's launcher: 56px square, placed `right` and `bottom` px from the viewport corner. */
export const LAUNCHER = { size: 56, right: 20, bottom: 20, gap: 12 } as const;

export async function chatAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export function supportChatOptions(input: {
  demo: boolean;
  userId: string;
  endpoint: string;
  bottom: number;
  page: () => string;
  onEvent?: SupportChatOptions['onEvent'];
  /** Routes a link in the chat to a dashboard page without reloading the app. */
  onNavigate?: SupportChatOptions['onNavigate'];
  /** The chat fills the window (the /chat page); links then open in new tabs so the chat stays. */
  standalone?: boolean;
  /** False starts the widget without its floating launcher; see `launcherShown`. */
  launcher?: boolean;
  /** The signed-in welcome (see `dashboardWelcome`), shown at once while a new chat is created. */
  welcome?: SupportChatOptions['welcome'];
}): SupportChatOptions {
  // The demo has no real session, so it chats like a website visitor (the worker allows dashboard origins on /chat).
  const account = !input.demo;
  return {
    endpoint: input.endpoint,
    mode: account ? 'account' : 'anonymous',
    ...(account && { getAccessToken: chatAccessToken }),
    // Per user, so on a shared browser the next person never sees someone else's draft or unread marker.
    storageKey: account ? `cs_dash_chat:${input.userId}` : 'cs_dash_chat_demo',
    page: input.page,
    subtitle: account ? 'Ask about CallSync or your workspace' : 'Ask us anything',
    offset: { bottom: input.bottom, right: LAUNCHER.right },
    zIndex: CHAT_LAYER,
    onEvent: input.onEvent,
    // The demo chats as a website visitor, so it gets the widget's website welcome.
    ...(account && input.welcome && { welcome: input.welcome }),
    ...(input.launcher === false && { launcher: false }),
    ...(input.standalone
      ? { layout: 'page' as const }
      : // The demo is chosen by ?demo in the address, so its new tab keeps it.
        { onNavigate: input.onNavigate, pageUrl: `/chat${input.demo ? withDemoSearch('') : ''}` }),
  };
}

/**
 * The welcome the worker gives a signed-in person's new chat: their first name, and the owner's chips for the
 * workspace's creator (who gets account tools), a team member's otherwise.
 */
export function dashboardWelcome(user: { id: string; metadata: Record<string, unknown> }, creatorId: string | null) {
  return {
    text: WELCOME.dashboard(welcomeName(user.metadata.full_name ?? user.metadata.name)),
    suggestions: creatorId === user.id ? WELCOME_SUGGESTIONS.owner : WELCOME_SUGGESTIONS.member,
  };
}

/** Setup's phone layout, as `@media (max-width: 767px)` in styles.css. */
export const PHONE_LAYOUT = '(max-width: 767px)';

/**
 * The dashboard has no floating chat launcher: it covered tables, buttons and setup steps on every page. The chat
 * opens from the account menu ("Chat with support"), the command menu, the Help page and setup's Help sheet.
 */
export const launcherShown = (_pathname: string, _phoneLayout: boolean) => false;

// The ticket topic for each troubleshooter (shared/content/troubleshooting), so a ticket raised from one starts sorted.
const TOPIC_CATEGORY: Record<string, TicketCategory> = {
  calls: 'bug',
  recordings: 'recordings',
  offline: 'bug',
  connect: 'setup',
  ai: 'bug',
  webhooks: 'bug',
};
export const ticketCategory = (topicId: string | undefined): TicketCategory | undefined =>
  topicId && Object.hasOwn(TOPIC_CATEGORY, topicId) ? TOPIC_CATEGORY[topicId] : undefined;

export type Box = { top: number; bottom: number; left: number; right: number; width: number; height: number };

/**
 * How far from the bottom edge the launcher sits so it stays clear of fixed bars under it (the recording player,
 * the setup action bar). Lifts are rounded up to 8px steps so small layout shifts never move it.
 */
export function launcherBottom(boxes: Box[], viewport: { width: number; height: number }): number {
  const { size, right, gap } = LAUNCHER;
  const left = viewport.width - right - size;
  let bottom: number = LAUNCHER.bottom;
  // Lowest first: clearing one bar can put the launcher on the next one up.
  for (const box of [...boxes].sort((a, b) => b.bottom - a.bottom)) {
    if (!box.width || !box.height || box.top >= viewport.height || box.bottom <= 0) continue;
    const top = viewport.height - bottom - size;
    const beside = box.right <= left - gap || box.left >= viewport.width - right + gap;
    const above = box.bottom <= top - gap;
    if (beside || above) continue;
    bottom = Math.ceil((viewport.height - box.top + gap) / 8) * 8;
  }
  // Something too tall to clear leaves the launcher reachable on screen rather than pushed off the top.
  return Math.min(bottom, Math.max(LAUNCHER.bottom, viewport.height - size - gap));
}

const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

/**
 * Starter text for a support chat message or ticket, built from what the person was troubleshooting. It only fills
 * the box; they add what happened and send it themselves. The workspace is left out: the signed-in chat carries it.
 */
export function supportChatPrefill(context: {
  issue: string;
  plan?: string;
  member?: string;
  checks?: string[];
  answers?: string[];
  ask?: string;
}): string {
  const lines = [`I need help with: ${clip(context.issue.trim(), 120)}`];
  if (context.plan) lines.push(`Plan: ${context.plan}`);
  if (context.member) lines.push(`Team member: ${clip(context.member, 200)}`);
  if (context.checks)
    lines.push(`Checks done: ${context.checks.length ? clip(context.checks.join('; '), 300) : 'none yet'}`);
  for (const answer of context.answers ?? []) lines.push(clip(answer, 160));
  const ask = context.ask ?? 'What happened: ';
  // A chat message takes 1,000 characters (a ticket 2,000); leave room for the person's own words after the prompt.
  return `${clip(lines.join('\n'), 799 - ask.length)}\n${ask}`;
}
